import { memo, useEffect, useRef, useState, useCallback } from "react";
import type { PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from "react";
// import { ScreenShareRequestDialog } from "@vc/views/dialogs/ScreenShareRequestDialog";
import { useNetworkQuality } from "@vc/viewmodels/network";
import { useBreakpoint } from "@vc/viewmodels/hooks/useBreakpoint";
import { useResponsiveDevice } from "@vc/viewmodels/hooks/useResponsiveDevice";
import { BottomSheet } from "@/clientWeb/components/ui/bottom-sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/clientWeb/components/ui/tooltip";
import type { LucideIcon } from "lucide-react";
import { MessageSquare, Users, Grid3X3, BarChart3, MonitorUp, Files, FileAudio, ClipboardList, Video as VideoIcon, Palette, Image, MoreHorizontal, CircleDot, HardDrive, LayoutGrid, Lock, Shield, Captions, Settings, FileText, Volume2, VolumeX
} from "lucide-react";
import { cn } from "@vc/lib/utils";
import { LeftSection, MicControl, CameraControl, ScreenShareControl, ReactionControl, AppsMenu, WebinarMenu, ChatControl, ControlButton, MoreOptionsMenu, LeaveButton, injectMicBarsAnimation, BreakoutRoomsControl, ParticipantsDropdown } from "./components";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import { useMeetingSessionStore } from "@/features/meeting/store/meeting-session.store";
import { BreakoutBroadcastControl } from "./components/breakout/BreakoutBroadcastControl"

export interface ControlBarProps {
  screenOn: boolean;
  chatOn: boolean;
  participantsOn: boolean;
  /** Any remote participant with their camera on. Nothing to pause without one. */
  anyRemoteCameraOn?: boolean;
  /** Any remote participant with their mic on. Nothing to pause without one. */
  anyRemoteMicOn?: boolean;
  webinarUsersOn?: boolean;
  /** Someone else in the room is recording. Shows the REC chip only, never this user's own Start/Stop. */
  someoneElseRecording?: boolean;
  /** This user's own cloud recording is running. */
  cloudRecordingOn?: boolean;
  cloudRecordingDuration?: string;
  /** A cloud start/stop is in flight, blocks a second click on Start. */
  cloudRecordingPending?: boolean;
  /** A cloud stop specifically is in flight: show a loader and block the
   *  Stop control until the mixer confirms. */
  cloudRecordingStopping?: boolean;
  onStopCloudRecording?: () => void;
  /** This user's own local (browser) recording is running. */
  localRecordingOn?: boolean;
  localRecordingDuration?: string;
  localRecordingPaused?: boolean;
  onPauseLocalRecording?: () => void;
  onResumeLocalRecording?: () => void;
  onStopLocalRecording?: () => void;
  permanentBottomBar?: boolean;
  onReact?: (emoji: string) => void;
  onToggleScreen: () => void;
  onToggleChat: () => void;
  onToggleParticipants: () => void;
  onToggleWebinarUsers?: () => void;
  onOpenMeetingInfo?: () => void;
  onToggleLock?: () => void;
  /** Host/co-host only: shows "End meeting for all" alongside "Leave". */
  canEndForAll?: boolean;
  onOpenAddVideoInput?: () => void;
  onStartCloudRecording?: () => void;
  onStartLocalRecording?: () => void;
  onAdjustView?: () => void;
  onToggleFullscreen?: () => void;
  onOpenSettings?: (tab: "audio" | "video" | "advanced") => void;
  onOpenHostControls?: () => void;
  onOpenShortcuts?: () => void;
  onOpenWhiteboard?: () => void;
  onOpenBreakoutRooms?: () => void;
  activeBreakoutRooms?: Array<{ id: string; name: string; participants: Array<{ id: string; name: string }>; isActive: boolean }>;
  currentBreakoutRoomId?: string | null;
  onJoinBreakoutRoom?: (roomId: string) => void;
  onLeaveBreakoutRoom?: () => void;
  onOpenBreakoutRoomsAssignment?: () => void;
  /** Viewer may open and run breakout rooms — keeps the button reachable
   *  before any room exists. */
  canManageBreakout?: boolean;
  /** The room has started them — the configuration option retires. */
  breakoutStarted?: boolean;
  /** Assigned a room they are not in yet. */
  breakoutJoinPending?: boolean;
  /** Where this participant has been put, so they can be offered a way in. */
  breakoutAssignedRoomId?: string | null;
  /** Close every breakout room and bring everybody back. */
  onEndBreakoutRooms?: () => void;
  /** Reopen the breakout join prompt. */
  onOpenBreakoutJoin?: () => void;
  /** `roomIds` empty = every room; `id` is echoed back so the sender can
   *  recognise their own message returning through the room they are in. */
  onBroadcastBreakoutMessage?: (message: string, roomIds: string[], id: string) => void;
  onOpenPolling?: () => void;
  onOpenSurveys?: () => void;
  onOpenBranding?: () => void;
  onOpenDocumentShare?: () => void;
  onOpenVideoShare?: () => void;
  /** Set while my request to present waits for the host: share entries show it and do nothing. */
  pendingRequestLabel?: string;
  onOpenLiveStreaming?: () => void;
  onOpenEngagementControls?: () => void;
  onOpenTranscription?: () => void;
  transcriptionActive?: boolean;
  onToggleNoiseCancellation?: () => void;
  onOpenBackgroundEffects?: () => void;
  // Document Picture-in-Picture for the participant grid, from More options.
  // Available whether or not anyone is sharing a screen.
  onToggleDocumentPiP?: () => void;
  onEndWebinarForAll?: () => void;
  onLeaveWebinar?: () => void;
  onEndMeetingForAll?: () => void;
  onLeaveMeeting?: () => void;
  onStartWebinar?: () => void;
  onStopWebinar?: () => void;
  isWebinar?: boolean;
  isWebinarLive?: boolean;
  meetingCode: string;
  /** Real meeting/webinar title from the persisted record; rendered in the
   *  left section of the bar and used as the info-button tooltip. */
  meetingName?: string;
  unreadChatCount?: number;
  meetingStartTime?: Date;
  viewerCount?: number;
  onVisibilityChange?: (visible: boolean) => void;
  isMobileView?: boolean; // NEW: For landscape/mobile unified behavior
  /** Host-controls permissions, supplied by the feature layer. The bar is
   *  purely presentational — it does not read any feature store directly. */
  /**
   * Joined through the meeting's passive link.
   *
   * Distinct from `hostCanUseMicrophone` and friends, which DISABLE their
   * buttons — a host restriction is something to see and understand, and often
   * to ask about, so the control stays visible and says why. Being a passive
   * attendee is not a restriction placed on you mid-meeting; it is what this
   * link is. There is nothing to appeal, and a row of dead buttons only invites
   * clicking them, so the controls are removed rather than greyed.
   */
  viewOnly?: boolean;
  hostCanUseMicrophone?: boolean;
  hostCanUseVideo?: boolean;
  hostCanShareScreen?: boolean;
  hostCanUseEmojis?: boolean;
  hostAllowChat?: boolean;
  /** Answers a click on a control the host has turned off. */
  onHostBlocked?: (what: 'microphone' | 'camera' | 'screenShare') => void;
  hostCanRaiseHand?: boolean;
}

export const ControlBar = memo(function ControlBar({
  screenOn,
  chatOn,
  participantsOn,
  anyRemoteCameraOn = false,
  anyRemoteMicOn = false,
  webinarUsersOn,
  someoneElseRecording,
  cloudRecordingOn,
  cloudRecordingDuration,
  cloudRecordingPending,
  cloudRecordingStopping,
  onStopCloudRecording,
  localRecordingOn,
  localRecordingDuration,
  localRecordingPaused,
  onPauseLocalRecording,
  onResumeLocalRecording,
  onStopLocalRecording,
  onVisibilityChange,
  onReact,
  onToggleScreen,
  onToggleChat,
  onToggleParticipants,
  onToggleWebinarUsers,
  onOpenMeetingInfo,
  onToggleLock,
  canEndForAll = false,
  onOpenAddVideoInput,
  onStartCloudRecording,
  onStartLocalRecording,
  onAdjustView,
  onToggleFullscreen,
  onOpenSettings,
  onOpenHostControls,
  onOpenShortcuts,
  onOpenWhiteboard,
  onOpenBreakoutRooms,
  activeBreakoutRooms = [],
  currentBreakoutRoomId,
  onJoinBreakoutRoom,
  onLeaveBreakoutRoom,
  onOpenBreakoutRoomsAssignment,
  canManageBreakout = false,
  breakoutStarted = false,
  breakoutJoinPending = false,
  breakoutAssignedRoomId = null,
  onEndBreakoutRooms,
  onOpenBreakoutJoin,
  onBroadcastBreakoutMessage,
  onOpenPolling,
  onOpenSurveys,
  onOpenBranding,
  onOpenDocumentShare,
  onOpenVideoShare,
  pendingRequestLabel,
  onOpenLiveStreaming,
  onOpenEngagementControls,
  onOpenTranscription,
  transcriptionActive,
  onToggleNoiseCancellation: _onToggleNoiseCancellation,
  onOpenBackgroundEffects,
  onToggleDocumentPiP,
  onEndWebinarForAll,
  onLeaveWebinar,
  onEndMeetingForAll,
  onLeaveMeeting,
  onStartWebinar,
  onStopWebinar,
  isWebinar,
  isWebinarLive,
  meetingCode,
  meetingName,
  unreadChatCount = 0,
  meetingStartTime,
  viewerCount = 0,
  permanentBottomBar,
  isMobileView,
  viewOnly = false,
  hostCanUseMicrophone = true,
  hostCanUseVideo = true,
  hostCanShareScreen = true,
  hostCanUseEmojis = true,
  hostAllowChat = true,
  onHostBlocked,
  hostCanRaiseHand = true,
}: ControlBarProps) {
  const captionsOn = useMeetingSessionStore((s) => s.captionsOn);
  const fullscreenOn = useMeetingSessionStore((s) => s.fullscreenOn);
  const isMyVideoPaused = useMeetingSessionStore((s) => s.isMyVideoPaused);
  const areIncomingVideosPaused = useMeetingSessionStore((s) => s.areIncomingVideosPaused);
  const areIncomingAudiosPaused = useMeetingSessionStore((s) => s.areIncomingAudiosPaused);
  const onToggleCaptions = useMeetingSessionStore((s) => s.toggleCaptions);
  const onPauseMyVideo = useMeetingSessionStore((s) => s.pauseMyVideo);
  const onResumeMyVideo = useMeetingSessionStore((s) => s.resumeMyVideo);
  const onToggleIncomingVideosPause = useMeetingSessionStore((s) => s.toggleIncomingVideosPause);
  const onToggleIncomingAudiosPause = useMeetingSessionStore((s) => s.toggleIncomingAudiosPause);
  // Lock state is owned by the session store so any consumer (control bar
  // chip, route guard, host overlay) reflects the same truth.
  const roomLocked = useMeetingSessionStore((s) => s.roomLocked);
  /**
   * Locking is the host's, and the ABSENCE of this callback is how that is
   * expressed — every surface below renders its lock control only when it is
   * present, and the feature layer passes it for moderators alone.
   *
   * There used to be a fallback here that flipped `roomLocked` in the store
   * when no callback was given. That defeated all three gates at once, because
   * a locally-defined wrapper is never undefined: the row rendered for
   * everyone, and a participant clicking it set the lock flag in their own
   * session — a room that showed LOCKED to them, was not locked for anybody
   * else, and had never been near the server. The server has always refused
   * the real call (`requireModerator`), so the only thing the fallback could
   * ever produce was that lie.
   */
  // Host permissions drive whether each control surface is rendered. The
  // values come in as props so the bar stays decoupled from any feature
  // store. The feature layer owns the wiring.
  const canShareScreen = hostCanShareScreen;
  const canUseEmojis = hostCanUseEmojis;
  const allowChat = hostAllowChat;
  const t = useTranslation();
  const networkQuality = useNetworkQuality()
  const { isMobile, isTablet } = useBreakpoint()
  const { isMobileDevice: isPhoneDevice } = useResponsiveDevice()
  const [showMoreSheet, setShowMoreSheet] = useState(false);
  const [showAppsSheet, setShowAppsSheet] = useState(false);
  // const [showScreenShareDialog, setShowScreenShareDialog] = useState(false);
  const [activeShareType, setActiveShareType] = useState<'tab' | 'window' | 'screen' | null>(null);

  // Unified mobile view - includes both portrait mobile and landscape mode
  const showMobileLayout = isMobileView || isMobile

  // The menu buttons in the scrolling row (reactions, the mic and camera
  // device pickers, breakout rooms) open on the press itself, as Radix menus
  // do, so a swipe that started on one opened it instead of scrolling the row.
  // For touch the press is set aside and the menu opens on the tap instead:
  // the browser sends no click for a touch that turned into a scroll.
  const lastPressWasTouchRef = useRef(false)
  const menuTriggerAt = (target: EventTarget) =>
    target instanceof Element ? target.closest('[aria-haspopup="menu"]') : null
  const onRowPointerDownCapture = (e: ReactPointerEvent) => {
    lastPressWasTouchRef.current = e.pointerType === 'touch'
    if (lastPressWasTouchRef.current && menuTriggerAt(e.target)) e.preventDefault()
  }
  const onRowClickCapture = (e: ReactMouseEvent) => {
    const trigger = menuTriggerAt(e.target)
    if (!trigger || !lastPressWasTouchRef.current) return
    lastPressWasTouchRef.current = false
    // The press Radix opens on, now that this is a tap and not a swipe.
    trigger.dispatchEvent(new PointerEvent('pointerdown', {
      bubbles: true, cancelable: true, button: 0, pointerType: 'mouse', isPrimary: true,
    }))
  }

  // Handle screen share button click - now uses dropdown menu
  const handleScreenShareClick = useCallback(() => {
    // Single toggle for both start and stop — onToggleScreen (toggleScreenShare)
    // opens the native picker when off and stops when on. (Previously the start
    // path was handled by a dropdown, which has been removed.)
    if (screenOn) setActiveShareType(null);
    onToggleScreen();
  }, [screenOn, onToggleScreen])

  // Handle screen share start from dropdown menu
  const handleStartScreenShare = useCallback((type: 'tab' | 'window' | 'screen') => {
    setActiveShareType(type);
    onToggleScreen();
  }, [onToggleScreen])

  // Auto-hide control bar state - disabled when permanentBottomBar is true
  const [isControlBarVisible, setIsControlBarVisible] = useState(true)
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Phones and landscape get longer: there the bar is brought back by a tap
  // that does nothing else, so hiding it too soon costs an extra tap.
  const INACTIVITY_DELAY = showMobileLayout ? 10000 : 5000
  // A sheet opened from the bar would lose its anchor if the bar hid under it.
  const sheetOpenRef = useRef(false)
  sheetOpenRef.current = showMoreSheet || showAppsSheet

  // Reset inactivity timer and show control bar (skip if permanentBottomBar is enabled)
  const resetInactivityTimer = useCallback(() => {
    // Skip auto-hide logic if permanent bottom bar is enabled
    if (permanentBottomBar) {
      setIsControlBarVisible(true)
      onVisibilityChange?.(true)
      return
    }

    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }

    // Show control bar immediately on activity
    setIsControlBarVisible(true)
    onVisibilityChange?.(true)

    // Set new timer to hide after inactivity. Not while one of the bar's menus
    // or sheets is open: it would be pulled out from under the person using it.
    const scheduleHide = () => {
      inactivityTimerRef.current = setTimeout(() => {
        if (sheetOpenRef.current || document.querySelector('[role="menu"]')) {
          scheduleHide()
          return
        }
        setIsControlBarVisible(false)
        onVisibilityChange?.(false)
      }, INACTIVITY_DELAY)
    }
    scheduleHide()
  }, [onVisibilityChange, permanentBottomBar, INACTIVITY_DELAY])

  // Set up event listeners for mouse and keyboard activity
  useEffect(() => {
    const handleMouseActivity = () => {
      resetInactivityTimer();
    };

    const handleKeyActivity = () => {
      resetInactivityTimer();
    };

    // Add event listeners
    window.addEventListener('mousemove', handleMouseActivity);
    window.addEventListener('mousedown', handleMouseActivity);
    window.addEventListener('keydown', handleKeyActivity);
    window.addEventListener('touchstart', handleMouseActivity);
    window.addEventListener('touchmove', handleMouseActivity);

    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleMouseActivity);
      window.removeEventListener('mousedown', handleMouseActivity);
      window.removeEventListener('keydown', handleKeyActivity);
      window.removeEventListener('touchstart', handleMouseActivity);
      window.removeEventListener('touchmove', handleMouseActivity);

      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [resetInactivityTimer]);
  // Start counting from mount (and again when the always-visible setting
  // changes), with the same menu and sheet guard as every later reset.
  useEffect(() => {
    resetInactivityTimer()
  }, [resetInactivityTimer])

  useEffect(() => {
    injectMicBarsAnimation();
  }, []);

  // Overflow menu items for mobile (Screen Share and Reactions shown directly in control bar)
  // When phone is detected, hide screen share, new input, and local recording
  const overflowItems: {
    icon: LucideIcon
    label: string
    onClick?: () => void
    active?: boolean
    color?: string
    /** Nothing for the action to act on — rendered, but not pressable. */
    disabled?: boolean
  }[] = [
    { icon: MessageSquare, label: t("controlBar.chat"), onClick: onToggleChat, active: chatOn, color: "text-green-500" },
    { icon: Users, label: t("controlBar.participants"), onClick: onToggleParticipants, active: participantsOn, color: "text-purple-500" },
    { icon: Grid3X3, label: t("controlBar.whiteboard"), onClick: onOpenWhiteboard, active: false, color: "text-orange-500", disabled: !!pendingRequestLabel },
    // Host only — the caller drops the handler for everyone else.
    ...(onOpenLiveStreaming
      ? [{ icon: MonitorUp, label: t("controlBar.liveStream"), onClick: onOpenLiveStreaming, active: false, color: "text-pink-500" }]
      : []),
    { icon: Settings, label: t("controlBar.settings"), onClick: () => onOpenSettings?.("audio"), active: false, color: "text-gray-600 dark:text-foreground" },
    // Only show on non-phone devices, and only when the screen offers it (the
    // camera is on). Without the guard this entry rendered with no handler.
    ...(isPhoneDevice || !onOpenAddVideoInput ? [] : [
      { icon: Image, label: t("controlBar.newInput"), onClick: onOpenAddVideoInput, active: false, color: "text-violet-500" },
    ]),
    // Cloud-recording item only renders when the host explicitly opted in
    // via the schedule-meeting advanced options (the feature layer drops
    // `onStartCloudRecording` when the flag is off), and when this session has
    // a meeting to record against.
    //
    // While a start or stop is in flight the click is swallowed rather than the
    // item hidden: removing it mid-interaction moves everything below it under
    // the user's cursor. Starting is not instant — it reaches the orchestrator
    // and then a mixer — so a second click is easy and would otherwise be sent.
    ...(onStartCloudRecording
      ? [{
          icon: CircleDot,
          label: cloudRecordingOn
            ? t("controlBar.stopCloudRecording")
            : isPhoneDevice ? t("controlBar.startRecording") : t("controlBar.cloudRecording"),
          onClick: cloudRecordingOn
            ? (cloudRecordingStopping ? () => { } : (onStopCloudRecording ?? (() => { })))
            : (cloudRecordingPending ? () => { } : onStartCloudRecording),
          active: cloudRecordingOn ?? false,
          color: "text-red-500",
        }]
      : []),
    // Captions only exist in a meeting with transcription; not offered elsewhere.
    ...(transcriptionActive
      ? [{ icon: Captions, label: captionsOn ? t("controlBar.turnOffCaptions") : t("controlBar.turnOnCaptions"), onClick: onToggleCaptions ?? (() => { }), active: captionsOn ?? false, color: "text-pink-500" }]
      : []),
    {
      icon: VideoIcon,
      label: isMyVideoPaused ? t("controlBar.resumeMyVideo") : t("controlBar.pauseMyVideo"),
      onClick: isMyVideoPaused ? onResumeMyVideo : onPauseMyVideo,
      active: isMyVideoPaused ?? false,
      color: "text-yellow-500",
    },
    {
      icon: VideoIcon,
      // "Videos", not the bare "Pause All Incoming" this used to say: the audio
      // item below sits next to it, and the label is also this item's React key.
      label: areIncomingVideosPaused ? t("controlBar.resumeAllVideos") : t("controlBar.pauseAllIncomingVideos"),
      onClick: onToggleIncomingVideosPause,
      active: areIncomingVideosPaused ?? false,
      // Offered as available whatever the room was doing, so a host looking at
      // a wall of avatars was invited to pause videos that were not playing.
      // Still available while paused, because that is how you resume.
      disabled: !anyRemoteCameraOn && !areIncomingVideosPaused,
      color: "text-orange-500",
    },
    {
      icon: areIncomingAudiosPaused ? Volume2 : VolumeX,
      label: areIncomingAudiosPaused ? t("controlBar.resumeAllAudio") : t("controlBar.pauseAllIncomingAudio"),
      onClick: onToggleIncomingAudiosPause,
      active: areIncomingAudiosPaused,
      // Same rule as videos: nothing to pause with every mic off, and always
      // available while paused, because that is how you resume.
      disabled: !anyRemoteMicOn && !areIncomingAudiosPaused,
      color: "text-orange-500",
    },
    // Only show on non-phone devices
    ...(isPhoneDevice ? [] : [
      {
        icon: HardDrive,
        label: localRecordingOn ? t("controlBar.stopLocalRecording") : t("controlBar.localRecording"),
        onClick: localRecordingOn ? (onStopLocalRecording ?? (() => { })) : onStartLocalRecording,
        active: localRecordingOn ?? false,
        color: "text-red-500",
      },
    ]),
  ];

  const appItems = [
    // Same gate as the MoreOptionsMenu entry: the feature layer drops the
    // callback for anyone who is not a moderator. Listed unconditionally — as
    // it was — this rendered an active Lock row for every attendee.
    ...(onToggleLock
      ? [{ icon: Lock, label: t("controlBar.lockRoom"), onClick: onToggleLock, color: "text-gray-600 dark:text-foreground" }]
      : []),
    ...(onOpenHostControls
      ? [{ icon: Shield, label: t("controlBar.hostControls"), onClick: onOpenHostControls, color: "text-purple-500" }]
      : []),
    { icon: Grid3X3, label: t("controlBar.breakout"), onClick: onOpenBreakoutRooms, color: "text-blue-500" },
    { icon: BarChart3, label: t("controlBar.polling"), onClick: onOpenPolling, color: "text-indigo-500" },
    // Surveys, Branding and Engagement are desktop-only: none of them is
    // built for a phone screen, so a phone browser does not offer them.
    ...(isPhoneDevice ? [] : [
      { icon: ClipboardList, label: t("controlBar.surveys"), onClick: onOpenSurveys, color: "text-indigo-500" },
    ]),
    { icon: Files, label: t("controlBar.documents"), onClick: onOpenDocumentShare, color: "text-teal-500", disabled: !!pendingRequestLabel },
    { icon: VideoIcon, label: t("controlBar.videoShare"), onClick: onOpenVideoShare, color: "text-cyan-500", disabled: !!pendingRequestLabel },
    ...(isPhoneDevice ? [] : [
      { icon: Palette, label: t("controlBar.branding"), onClick: onOpenBranding, color: "text-amber-500" },
      { icon: BarChart3, label: t("controlBar.engagement"), onClick: onOpenEngagementControls, color: "text-green-500" },
    ]),
    // Same gate as the desktop AppsMenu — only show transcription when the
    // session-level flag is on (the feature layer drops the callback).
    ...(onOpenTranscription
      ? [{ icon: FileAudio, label: t("controlBar.transcription"), onClick: onOpenTranscription, color: "text-cyan-500" }]
      : []),
  ];

  return (
    <div className="relative w-full px-4">
      {/* Desktop/Tablet: Traditional ControlBar - Only when NOT in mobile/landscape view */}
      {!showMobileLayout && (
        <div
          className={cn(
            "flex h-14 w-full pt-1 items-center justify-between bg-vc-control-bg px-4 transition-all duration-500 ease-in-out",
            isControlBarVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
          )}
        >
          {/* Left Section */}
          <LeftSection
            meetingStartTime={meetingStartTime}
            meetingCode={meetingCode}
            meetingName={meetingName}
            networkQuality={networkQuality}
            onOpenMeetingInfo={onOpenMeetingInfo}
            compact={isTablet}
            othersRecording={someoneElseRecording}
            cloudRecordingOn={cloudRecordingOn}
            cloudRecordingDuration={cloudRecordingDuration}
            cloudRecordingStopping={cloudRecordingStopping}
            onStopCloudRecording={cloudRecordingOn ? onStopCloudRecording : undefined}
            localRecordingOn={localRecordingOn}
            localRecordingDuration={localRecordingDuration}
            localRecordingPaused={localRecordingPaused}
            onPauseLocalRecording={localRecordingOn ? onPauseLocalRecording : undefined}
            onResumeLocalRecording={localRecordingOn ? onResumeLocalRecording : undefined}
            onStopLocalRecording={localRecordingOn ? onStopLocalRecording : undefined}
            viewerCount={viewerCount}
          />

          {/* Right - Control Buttons */}
          <div className="flex items-center gap-2">
               {roomLocked && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* Everyone sees that the room is locked; only a host can
                        undo it. Rendered as a plain badge without the callback
                        rather than a disabled button, so it reads as the status
                        indicator it is instead of an action being withheld. */}
                    {onToggleLock ? (
                      <button
                        type="button"
                        onClick={onToggleLock}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-500/15 text-red-300 hover:bg-red-500/25 transition-colors animate-pulse"
                        aria-label={roomLocked ? t("controlBar.unlockRoom") : t("controlBar.lockRoom")}
                      >
                        <Lock className="w-3.5 h-3.5" />
                        <span className="text-[11px] font-semibold uppercase tracking-wide">
                          {t("controlBar.locked")}
                        </span>
                      </button>
                    ) : (
                      <span
                        className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-500/15 text-red-300 animate-pulse"
                        aria-label={t("controlBar.locked")}
                      >
                        <Lock className="w-3.5 h-3.5" />
                        <span className="text-[11px] font-semibold uppercase tracking-wide">
                          {t("controlBar.locked")}
                        </span>
                      </span>
                    )}
                  </TooltipTrigger>
                  <TooltipContent side="top" sideOffset={8} className="border-none capitalize">
                    {/* "Unlock room" only where unlocking is possible. For a
                        participant the chip is a status, so offering the verb
                        describes an action they do not have. */}
                    {onToggleLock ? t("controlBar.unlockRoom") : t("controlBar.locked")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {transcriptionActive && (
              <div className="relative flex mr-2 items-center justify-center h-5 w-5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400/30"></span>
                <FileText className="h-5 w-5 text-cyan-400 animate-pulse" />
              </div>
            )}
            {/* Everything between here and Leave belongs to taking part. A
                view-only attendee is here to watch, so the bar keeps only what
                still means something to them: the meeting's identity on the
                left, and the way out on the right. */}
            {!viewOnly && (<>
            {!viewOnly && (
              <MicControl
                onOpenSettings={onOpenSettings}
                disabled={!hostCanUseMicrophone}
                onBlocked={onHostBlocked ? () => onHostBlocked('microphone') : undefined}
              />
            )}
            {/* Blinking lock chip rendered immediately beside the mic so the
                host can see the room-locked state at a glance. Clicking it
                toggles the lock off, mirroring the menu item. */}
         
            {!viewOnly && (
              <CameraControl
                onOpenSettings={onOpenSettings}
                disabled={!hostCanUseVideo}
                onBlocked={onHostBlocked ? () => onHostBlocked('camera') : undefined}
              />
            )}
            {/* Always rendered, like mic and camera. Hiding it left a
                participant with no way to discover the feature exists, and no
                way to ask for it. */}
            {!viewOnly && (
              <ScreenShareControl
                screenOn={screenOn}
                onToggleScreen={handleScreenShareClick}
                onStartScreenShare={handleStartScreenShare}
                activeShareType={activeShareType}
                blocked={!canShareScreen || !!pendingRequestLabel}
                blockedLabel={pendingRequestLabel}
                onBlocked={onHostBlocked ? () => onHostBlocked('screenShare') : undefined}
              />
            )}
            {canUseEmojis && (
              <ReactionControl
                onReact={onReact}
                canRaiseHand={hostCanRaiseHand}
              />
            )}
            {/* A participant's way back to the host's announcements. The host
                reads the same thread from inside their rooms control below;
                a participant has none, so without this an announcement was
                readable exactly once, while its toast was on screen. */}
            {breakoutStarted && !canManageBreakout && <BreakoutBroadcastControl />}
            {/* The moderator's rooms control. Participants do not get one: the
                only thing it offers them is joining the room they were put in,
                and they are already told that by the prompt and by the Join
                Room entry under Apps. A control beside Reactions that opens a
                menu of rooms they may not move between is furniture. */}
            {breakoutStarted && canManageBreakout && <BreakoutRoomsControl
              activeBreakoutRooms={activeBreakoutRooms}
              currentBreakoutRoomId={currentBreakoutRoomId}
              onJoinRoom={onJoinBreakoutRoom}
              onLeaveRoom={onLeaveBreakoutRoom}
              onOpenManage={onOpenBreakoutRoomsAssignment}
              canManage={canManageBreakout}
              started={breakoutStarted}
              assignedRoomId={breakoutAssignedRoomId}
              onEndRooms={onEndBreakoutRooms}
              onBroadcastMessage={onBroadcastBreakoutMessage}
            />}
            <AppsMenu
              onOpenWhiteboard={onOpenWhiteboard}
              onOpenBreakoutRooms={onOpenBreakoutRooms}
              canManageBreakout={canManageBreakout}
              breakoutStarted={breakoutStarted}
              breakoutJoinPending={breakoutJoinPending}
              onOpenBreakoutJoin={onOpenBreakoutJoin}
              onOpenPolling={onOpenPolling}
              onOpenDocumentShare={onOpenDocumentShare}
              onOpenVideoShare={onOpenVideoShare}
              pendingRequestLabel={pendingRequestLabel}
              onOpenLiveStreaming={onOpenLiveStreaming}
              onOpenTranscription={onOpenTranscription}
            />
            {/* Webinar-only, and only for a webinar session. In an ordinary
                meeting none of these do anything, so the button is absent
                rather than present and inert. */}
            {isWebinar && (
              <WebinarMenu
                onStartWebinar={onStartWebinar}
                onStopWebinar={onStopWebinar}
                // A phone in landscape can get this bar; same desktop-only rule
                // as the Apps sheet, and WebinarMenu drops an item with no callback.
                onOpenSurveys={isPhoneDevice ? undefined : onOpenSurveys}
                onOpenBranding={isPhoneDevice ? undefined : onOpenBranding}
                onOpenEngagementControls={isPhoneDevice ? undefined : onOpenEngagementControls}
                isWebinarLive={isWebinarLive}
              />
            )}
            {allowChat && (
              <ChatControl chatOn={chatOn} unreadChatCount={unreadChatCount} onToggleChat={onToggleChat} />
            )}
            {/* Participants control:
                - Meeting: direct toggle, opens the participants side panel.
                - Webinar: dropdown menu with Participants + Webinar Users,
                  each opens its dedicated side panel. */}
            {isWebinar ? (
              <ParticipantsDropdown
                participantsOn={participantsOn}
                webinarUsersOn={webinarUsersOn}
                onToggleParticipants={onToggleParticipants}
                onToggleWebinarUsers={onToggleWebinarUsers}
              />
            ) : (
              <ControlButton
                active={participantsOn}
                onClick={onToggleParticipants}
                label={t("controlBar.participants")}
              >
                <Users className="h-5 w-5 text-white" />
              </ControlButton>
            )}
            {/* Toggle for whether this viewer sees captions on their own
                screen — separate from the meeting's server-side transcription,
                which needs no toggle here (it runs automatically whenever the
                meeting enabled it). Shown only in such a meeting: captions come
                from that transcription alone, so elsewhere there is nothing to
                show and the button is not offered at all. */}
            {transcriptionActive && (
              <ControlButton
                active={captionsOn}
                onClick={onToggleCaptions}
                label={captionsOn ? t("controlBar.turnOffCaptions") : t("controlBar.turnOnCaptions")}
              >
                <Captions className="h-5 w-5 text-white" />
              </ControlButton>
            )}
            <MoreOptionsMenu
              fullscreenOn={fullscreenOn}
              captionsOn={captionsOn}
              roomLocked={roomLocked}
              onOpenSettings={() => onOpenSettings?.("audio")}
              onToggleLock={onToggleLock}
              onOpenHostControls={onOpenHostControls}
              onOpenAddVideoInput={onOpenAddVideoInput}
              onOpenShortcuts={onOpenShortcuts}
              onAdjustView={onAdjustView}
              onToggleCaptions={transcriptionActive ? onToggleCaptions : undefined}
              cloudRecordingOn={cloudRecordingOn}
              cloudRecordingStopping={cloudRecordingStopping}
              onStopCloudRecording={onStopCloudRecording}
              onStartCloudRecording={onStartCloudRecording}
              localRecordingOn={localRecordingOn}
              onStopLocalRecording={onStopLocalRecording}
              onStartLocalRecording={onStartLocalRecording}
              onOpenBackgroundEffects={onOpenBackgroundEffects}
              // No Document PiP on phone browsers, and element PiP there floats a
              // single bare tile with no controls. Absent rather than half-working.
              onTogglePictureInPicture={isPhoneDevice ? undefined : onToggleDocumentPiP}
              onToggleFullscreen={onToggleFullscreen}
              isMyVideoPaused={isMyVideoPaused}
              areIncomingVideosPaused={areIncomingVideosPaused}
              anyRemoteCameraOn={anyRemoteCameraOn}
              onPauseMyVideo={onPauseMyVideo}
              onResumeMyVideo={onResumeMyVideo}
              onToggleIncomingVideosPause={onToggleIncomingVideosPause}
              areIncomingAudiosPaused={areIncomingAudiosPaused}
              anyRemoteMicOn={anyRemoteMicOn}
              onToggleIncomingAudiosPause={onToggleIncomingAudiosPause}
            />
            </>)}
            <LeaveButton
              onEndWebinarForAll={onEndWebinarForAll}
              onLeaveWebinar={onLeaveWebinar}
              onEndMeetingForAll={onEndMeetingForAll}
              onLeaveMeeting={onLeaveMeeting}
              isWebinar={isWebinar}
              canEndForAll={canEndForAll}
              inBreakoutRoom={!!currentBreakoutRoomId}
              onLeaveBreakoutRoom={onLeaveBreakoutRoom}
            />
          </div>
        </div>
      )}

      {/* Mobile/Landscape: Floating Glassmorphism ControlBar with Emoji & Screen Share */}
      {showMobileLayout && (
        <div className="flex items-center gap-2 w-full">
          {/* Left: Meeting Info - Sticky */}
          <div className="flex-shrink-0">
            <LeftSection
              meetingStartTime={meetingStartTime}
              meetingCode={meetingCode}
              meetingName={meetingName}
              networkQuality={networkQuality}
              onOpenMeetingInfo={onOpenMeetingInfo}
              compact={true}
              othersRecording={someoneElseRecording}
              cloudRecordingOn={cloudRecordingOn}
              cloudRecordingDuration={cloudRecordingDuration}
              cloudRecordingStopping={cloudRecordingStopping}
              onStopCloudRecording={cloudRecordingOn ? onStopCloudRecording : undefined}
              localRecordingOn={localRecordingOn}
              localRecordingDuration={localRecordingDuration}
              localRecordingPaused={localRecordingPaused}
              onPauseLocalRecording={localRecordingOn ? onPauseLocalRecording : undefined}
              onResumeLocalRecording={localRecordingOn ? onResumeLocalRecording : undefined}
              onStopLocalRecording={localRecordingOn ? onStopLocalRecording : undefined}
              viewerCount={viewerCount}
            />
          </div>

          {/* Center: Essential Controls - Horizontally Scrollable */}
          {/* Dropped whole for a view-only attendee — see the desktop bar. */}
          {!viewOnly && (
          <div
            className="flex-1 overflow-x-auto scrollbar-hide scroll-smooth touch-pan-x overscroll-x-contain"
            onPointerDownCapture={onRowPointerDownCapture}
            onClickCapture={onRowClickCapture}
          >
            <div className="flex items-center justify-end gap-2 min-w-max px-1">
              {!viewOnly && (
                <MicControl
                  compact
                  disabled={!hostCanUseMicrophone}
                  onBlocked={onHostBlocked ? () => onHostBlocked('microphone') : undefined}
                />
              )}
              {!viewOnly && (
                <CameraControl
                  compact
                  disabled={!hostCanUseVideo}
                  onBlocked={onHostBlocked ? () => onHostBlocked('camera') : undefined}
                />
              )}
              {/* Only show screen share on non-phone devices in mobile layout */}
              {!isPhoneDevice && !viewOnly && (
                <ScreenShareControl
                  screenOn={screenOn}
                  onToggleScreen={handleScreenShareClick}
                  onStartScreenShare={handleStartScreenShare}
                  activeShareType={activeShareType}
                  compact
                  blocked={!canShareScreen || !!pendingRequestLabel}
                  blockedLabel={pendingRequestLabel}
                  onBlocked={onHostBlocked ? () => onHostBlocked('screenShare') : undefined}
                />
              )}
              {canUseEmojis && (
                <ReactionControl
                  onReact={onReact}
                  canRaiseHand={hostCanRaiseHand}
                  compact
                />
              )}
              {/* Moderators only, and only once there are rooms — see the
                  desktop control above. This one rendered unconditionally. */}
              {breakoutStarted && canManageBreakout && (
                <BreakoutRoomsControl
                  activeBreakoutRooms={activeBreakoutRooms}
                  currentBreakoutRoomId={currentBreakoutRoomId}
                  onJoinRoom={onJoinBreakoutRoom}
                  onLeaveRoom={onLeaveBreakoutRoom}
                  onOpenManage={onOpenBreakoutRoomsAssignment}
                  canManage={canManageBreakout}
                  started={breakoutStarted}
                  assignedRoomId={breakoutAssignedRoomId}
                  onEndRooms={onEndBreakoutRooms}
                  onBroadcastMessage={onBroadcastBreakoutMessage}
                  compact
                />
              )}

              {/* Apps Button to open Apps Bottom Sheet */}
              <button
                onClick={() => setShowAppsSheet(true)}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-card/10 text-white hover:bg-card/20 transition flex-shrink-0"
              >
                <LayoutGrid className="h-5 w-5" />
              </button>

              {/* More Button to open Meeting Controls Bottom Sheet */}
              <button
                onClick={() => setShowMoreSheet(true)}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-card/10 text-white hover:bg-card/20 transition flex-shrink-0"
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>
            </div>
          </div>
          )}

          {/* Right: Leave Button - Sticky */}
          <div className="flex-shrink-0">
            <LeaveButton
              onEndWebinarForAll={onEndWebinarForAll}
              onLeaveWebinar={onLeaveWebinar}
              onEndMeetingForAll={onEndMeetingForAll}
              onLeaveMeeting={onLeaveMeeting}
              isWebinar={isWebinar}
              canEndForAll={canEndForAll}
              inBreakoutRoom={!!currentBreakoutRoomId}
              onLeaveBreakoutRoom={onLeaveBreakoutRoom}
              compact
            />
          </div>
        </div>
      )}

      {/* Mobile: Bottom Sheet for Meeting Controls */}
      <BottomSheet
        open={showMoreSheet}
        onClose={() => setShowMoreSheet(false)}
        title={t("controlBar.meetingControls")}
      >
        <div className="grid grid-cols-3 gap-4">
          {overflowItems.map((item) => (
            <button
              key={item.label}
              disabled={item.disabled}
              onClick={() => {
                item.onClick?.();
                setShowMoreSheet(false);
              }}
              className={cn(
                "flex flex-col items-center gap-2 p-4 rounded-xl transition",
                item.active
                  ? "bg-blue-500 text-white"
                  // gray-700 alone put dark labels on the dark-mode sheet.
                  : "bg-primary-100/20 text-gray-700 hover:bg-gray-200 dark:text-foreground dark:hover:bg-white/10",
                // The flag was already on the item and nothing read it, so a
                // disabled action looked and behaved exactly like a live one.
                item.disabled && "opacity-40 cursor-not-allowed hover:bg-primary-100/20 dark:hover:bg-primary-100/20"
              )}
            >
              <item.icon className={cn("h-4 w-4", !item.active && item.color)} />
              <span className="text-xs font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </BottomSheet>

      {/* Mobile: Bottom Sheet for Apps */}
      <BottomSheet
        open={showAppsSheet}
        onClose={() => setShowAppsSheet(false)}
        title={t("controlBar.apps")}
      >
        <div className="grid grid-cols-3 gap-4">
          {appItems.map((item) => (
            <button
              key={item.label}
              disabled={"disabled" in item && !!item.disabled}
              title={"disabled" in item && item.disabled ? pendingRequestLabel : undefined}
              onClick={() => {
                item.onClick?.();
                setShowAppsSheet(false);
              }}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-primary-100/20 text-gray-700 hover:bg-gray-200 dark:hover:bg-white/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <item.icon className={cn("h-4 w-4", item.color)} />
              <span className="text-xs font-medium dark:text-foreground">{item.label}</span>
            </button>
          ))}
        </div>
      </BottomSheet>

      {/* Screen Share Request Dialog - Commented out as requested */}
      {/* <ScreenShareRequestDialog
        open={showScreenShareDialog}
        onOpenChange={setShowScreenShareDialog}
        onRequest={() => {
          console.log("Screen share requested")
        }}
        onApproved={handleScreenShareApproved}
        onRejected={() => {
          console.log("Screen share request rejected")
        }}
      /> */}

    </div>
  );
});
